/**
 * Planer i RCP — wspólne teksty pod `pln.common.*`, `pln.enums.*`, `pln.wt.*`, `pln.alerts.*`, `pln.errors.*` (plik FUNDAMENTU).
 * Obszary mogą DOPISYWAĆ brakujące etykiety (bez zmiany istniejących); resztę trzymają w `planner-grid.ts` (planer)
 * i `planner-worktime.ts` (RCP).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const plnCommonPl = {
  nav: {
    planner: 'Planer',
    setup: 'Czynności i atrybuty',
    worktime: 'RCP',
    myWorktime: 'Mój czas pracy',
    settings: 'Reguły czasu pracy',
  },
  module: 'Planer i czas pracy',
  hoursShort: 'h',
  demoBadge: 'Dane demo',
};

export const plnCommonEn: Shape<typeof plnCommonPl> = {
  nav: {
    planner: 'Planner',
    setup: 'Activities & attributes',
    worktime: 'Time & attendance',
    myWorktime: 'My working time',
    settings: 'Working-time rules',
  },
  module: 'Planner & working time',
  hoursShort: 'h',
  demoBadge: 'Demo data',
};

export const plnEnumsPl = {
  shiftType: { MORNING: 'Ranna', AFTERNOON: 'Popołudniowa', NIGHT: 'Nocna', FULL_DAY: 'Dzienna' },
  shiftStatus: { DRAFT: 'Szkic', CONFIRMED: 'Potwierdzona', IN_PROGRESS: 'W toku', COMPLETED: 'Zakończona' },
  taskType: {
    VISITS: 'Wizyty u klientów',
    DELIVERY_ROUTE: 'Trasa dostaw',
    WAREHOUSE: 'Magazyn',
    OFFICE: 'Biuro',
    TRAINING: 'Szkolenie',
    MEETING: 'Spotkanie',
    TASK: 'Czynność',
  },
  taskPriority: { LOW: 'Niski', MEDIUM: 'Średni', HIGH: 'Wysoki' },
  taskStatus: { PENDING: 'Do zrobienia', IN_PROGRESS: 'W toku', DONE: 'Wykonane', SKIPPED: 'Pominięte', MISSED: 'Niewykonane' },
  schedulingHint: {
    FIXED_TIME: 'O stałej godzinie',
    BEFORE_FIRST_TASK: 'Przed pierwszą czynnością',
    AFTER_LAST_TASK: 'Po ostatniej czynności',
    RELATIVE_TO_SHIFT_START: 'Względem początku zmiany',
    RELATIVE_TO_SHIFT_END: 'Względem końca zmiany',
  },
  requestType: {
    VACATION: 'Urlop',
    SICK: 'Zwolnienie lekarskie',
    BUSINESS_TRIP: 'Delegacja',
    TRAINING_EXT: 'Szkolenie zewnętrzne',
    SHIFT_SWAP: 'Zamiana zmiany',
    AVAILABILITY: 'Dyspozycyjność',
    PREFERENCE: 'Preferencja zmiany',
  },
  requestStatus: { PENDING: 'Oczekuje', APPROVED: 'Zatwierdzony', REJECTED: 'Odrzucony', CANCELLED: 'Anulowany' },
  attributeSource: { LIST: 'Lista wartości', VEHICLE: 'Pojazd z Floty', TEXT: 'Tekst' },
  absence: {
    UW: 'Urlop wypoczynkowy',
    UZ: 'Urlop na żądanie',
    CH: 'Zwolnienie lekarskie',
    OP: 'Opieka nad dzieckiem',
    OK: 'Urlop okolicznościowy',
    DEL: 'Delegacja',
    SZK: 'Szkolenie',
    UB: 'Urlop bezpłatny',
    NN: 'Nieobecność nieusprawiedliwiona',
  },
  workTimeSystem: { BASIC: 'Podstawowy', EQUIVALENT: 'Równoważny', TASK_BASED: 'Zadaniowy' },
  periodStatus: { OPEN: 'Otwarty', CLOSED: 'Zamknięty' },
  entrySource: { PLAN: 'Z grafiku', MANUAL: 'Korekta', APP_CLOCK: 'Odbicie w apce', VISIT: 'Z wizyt', LOGBOOK: 'Z karty drogowej' },
  entryStatus: { DRAFT: 'Roboczy', OPEN: 'Otwarte odbicie', CONFIRMED: 'Potwierdzony' },
  dayCode: { WORK: 'Praca', FREE: 'Wolne', SUNDAY: 'Niedziela', HOLIDAY: 'Święto', ABSENCE: 'Nieobecność' },
};

export const plnEnumsEn: Shape<typeof plnEnumsPl> = {
  shiftType: { MORNING: 'Morning', AFTERNOON: 'Afternoon', NIGHT: 'Night', FULL_DAY: 'Day' },
  shiftStatus: { DRAFT: 'Draft', CONFIRMED: 'Confirmed', IN_PROGRESS: 'In progress', COMPLETED: 'Completed' },
  taskType: {
    VISITS: 'Customer visits',
    DELIVERY_ROUTE: 'Delivery route',
    WAREHOUSE: 'Warehouse',
    OFFICE: 'Office',
    TRAINING: 'Training',
    MEETING: 'Meeting',
    TASK: 'Activity',
  },
  taskPriority: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' },
  taskStatus: { PENDING: 'To do', IN_PROGRESS: 'In progress', DONE: 'Done', SKIPPED: 'Skipped', MISSED: 'Missed' },
  schedulingHint: {
    FIXED_TIME: 'At a fixed time',
    BEFORE_FIRST_TASK: 'Before the first activity',
    AFTER_LAST_TASK: 'After the last activity',
    RELATIVE_TO_SHIFT_START: 'Relative to shift start',
    RELATIVE_TO_SHIFT_END: 'Relative to shift end',
  },
  requestType: {
    VACATION: 'Leave',
    SICK: 'Sick leave',
    BUSINESS_TRIP: 'Business trip',
    TRAINING_EXT: 'External training',
    SHIFT_SWAP: 'Shift swap',
    AVAILABILITY: 'Availability',
    PREFERENCE: 'Shift preference',
  },
  requestStatus: { PENDING: 'Pending', APPROVED: 'Approved', REJECTED: 'Rejected', CANCELLED: 'Cancelled' },
  attributeSource: { LIST: 'List of values', VEHICLE: 'Fleet vehicle', TEXT: 'Text' },
  absence: {
    UW: 'Annual leave',
    UZ: 'Leave on demand',
    CH: 'Sick leave',
    OP: 'Childcare leave',
    OK: 'Special leave',
    DEL: 'Business trip',
    SZK: 'Training',
    UB: 'Unpaid leave',
    NN: 'Unexcused absence',
  },
  workTimeSystem: { BASIC: 'Basic', EQUIVALENT: 'Equivalent', TASK_BASED: 'Task-based' },
  periodStatus: { OPEN: 'Open', CLOSED: 'Closed' },
  entrySource: { PLAN: 'From schedule', MANUAL: 'Correction', APP_CLOCK: 'App clock-in', VISIT: 'From visits', LOGBOOK: 'From logbook' },
  entryStatus: { DRAFT: 'Draft', OPEN: 'Open clock-in', CONFIRMED: 'Confirmed' },
  dayCode: { WORK: 'Work', FREE: 'Day off', SUNDAY: 'Sunday', HOLIDAY: 'Holiday', ABSENCE: 'Absence' },
};

/** Komunikaty kontroli grafiku (`WtViolation.messageKey` = `wt.r2.dailyRest` → `pln.wt.r2.dailyRest`). */
export const plnWtPl = {
  r2: { dailyRest: 'Odpoczynek dobowy {restH} h — mniej niż {minH} h' },
  r4: {
    weeklyWarn: 'Tygodniowo {weeklyH} h — ponad {capH} h',
    weeklyBlock: 'Tygodniowo {weeklyH} h — przekroczony limit {capH} h',
  },
  r5: { consecutiveDays: '{days} dni pracy z rzędu — więcej niż {maxDays}' },
  blockTitle: 'Zapis łamie regułę czasu pracy',
  overrideReason: 'Powód zapisu mimo naruszenia',
};

export const plnWtEn: Shape<typeof plnWtPl> = {
  r2: { dailyRest: 'Daily rest {restH} h — less than {minH} h' },
  r4: {
    weeklyWarn: '{weeklyH} h this week — over {capH} h',
    weeklyBlock: '{weeklyH} h this week — limit of {capH} h exceeded',
  },
  r5: { consecutiveDays: '{days} working days in a row — more than {maxDays}' },
  blockTitle: 'This save breaks a working-time rule',
  overrideReason: 'Reason for saving despite the breach',
};

/** Alerty zgodności RCP (`ComplianceAlert.messageKey`). */
export const plnAlertsPl = {
  restDaily: 'Odpoczynek dobowy {hours} h {minutes} min — mniej niż 11 h (KP art. 132)',
  restWeekly: 'Tydzień {week}: praca przez 7 dni — możliwe naruszenie 35 h odpoczynku tygodniowego (KP art. 133)',
  sundayHolidayNoOff: 'Tydzień {week}: praca w niedzielę lub święto bez oddanego dnia wolnego (KP art. 151¹¹)',
  overtimeLimitExceeded: 'Nadgodziny {overtimeH} h przekraczają roczny limit {limitH} h (KP art. 151 §3)',
  overtimeLimitNear: 'Wykorzystano {pct}% rocznego limitu nadgodzin ({overtimeH} h z {limitH} h)',
};

export const plnAlertsEn: Shape<typeof plnAlertsPl> = {
  restDaily: 'Daily rest {hours} h {minutes} min — less than 11 h (Labour Code art. 132)',
  restWeekly: 'Week {week}: 7 working days — possible breach of the 35 h weekly rest (Labour Code art. 133)',
  sundayHolidayNoOff: 'Week {week}: Sunday or holiday work without a compensating day off (Labour Code art. 151¹¹)',
  overtimeLimitExceeded: 'Overtime {overtimeH} h exceeds the annual limit of {limitH} h (Labour Code art. 151 §3)',
  overtimeLimitNear: '{pct}% of the annual overtime limit used ({overtimeH} h of {limitH} h)',
};

/** Kody błędów API modułu (`code` w ciele odpowiedzi 4xx). Obszary dopisują swoje. */
export const plnErrorsPl = {
  WORKING_TIME_BLOCK: 'Zapis łamie regułę czasu pracy — podaj powód albo zmień godziny',
  VEHICLE_DOUBLE: 'Pojazd jest już przydzielony na inną zmianę w tym czasie',
  PERSON_OVERLAP: 'Ta osoba ma już zmianę w tym czasie',
  ABSENCE: 'Ta osoba ma w tym dniu nieobecność',
  TASK_OUTSIDE_SHIFT: 'Czynność wychodzi poza godziny zmiany',
  OPEN_TOO_LONG: 'Odbicie jest otwarte dłużej niż 16 h — poproś kierownika o korektę',
  PERIOD_CLOSED: 'Okres rozliczeniowy jest zamknięty',
  ALREADY_CLOCKED_IN: 'Wejście jest już zarejestrowane',
  MANUAL_ENTRY: 'Ten dzień poprawił kierownik — odbicie nie jest możliwe',
  NOT_CLOCKED_IN: 'Brak zarejestrowanego wejścia',
  OCCURRED_AT_FUTURE: 'Czas odbicia jest z przyszłości — sprawdź zegar telefonu',
  OCCURRED_AT_TOO_OLD: 'Odbicie jest starsze niż 48 h — poproś kierownika o korektę',
  CLOCK_REPLAY_FOREIGN: 'To odbicie należy do innego użytkownika',
  REASON_REQUIRED: 'Podaj powód korekty',
  INVALID_RANGE: 'Nieprawidłowy zakres godzin lub dat',
  INVALID_ABSENCE: 'Nieznany rodzaj nieobecności',
  USER_NOT_FOUND: 'Nie znaleziono pracownika',
  ENTRY_NOT_FOUND: 'Nie znaleziono wpisu',
  PERIOD_NOT_FOUND: 'Nie znaleziono okresu rozliczeniowego',
};

export const plnErrorsEn: Shape<typeof plnErrorsPl> = {
  WORKING_TIME_BLOCK: 'This save breaks a working-time rule — give a reason or change the hours',
  VEHICLE_DOUBLE: 'The vehicle is already assigned to another shift at this time',
  PERSON_OVERLAP: 'This person already has a shift at this time',
  ABSENCE: 'This person is absent on that day',
  TASK_OUTSIDE_SHIFT: 'The activity falls outside the shift hours',
  OPEN_TOO_LONG: 'The clock-in has been open for more than 16 h — ask your manager for a correction',
  PERIOD_CLOSED: 'The settlement period is closed',
  ALREADY_CLOCKED_IN: 'You are already clocked in',
  MANUAL_ENTRY: 'Your manager corrected this day — clocking is not possible',
  NOT_CLOCKED_IN: 'No clock-in recorded',
  OCCURRED_AT_FUTURE: 'The clock time is in the future — check the phone clock',
  OCCURRED_AT_TOO_OLD: 'The clock event is older than 48 h — ask your manager for a correction',
  CLOCK_REPLAY_FOREIGN: 'This clock event belongs to another user',
  REASON_REQUIRED: 'Give a reason for the correction',
  INVALID_RANGE: 'Invalid hours or date range',
  INVALID_ABSENCE: 'Unknown absence type',
  USER_NOT_FOUND: 'Employee not found',
  ENTRY_NOT_FOUND: 'Entry not found',
  PERIOD_NOT_FOUND: 'Settlement period not found',
};
